import { PrimaryButton, FlowGraph } from '@mmsg/louie'
import { Icon } from 'icons'
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { isLoggedIn } from '../../services/authenticationService'
import { formatCurrency } from '../../helpers'
import LogoutButton from 'components/logoutButton'
import ConsultantLogoutButton from 'components/pages/consultant/consultantLogoutButton'
import ContactUsDialog from '../dialog/contactUsDialog'
import { useAccordionToggle } from 'react-bootstrap/AccordionToggle'
import useComponentVisible from 'helpers/useComponentVisible'
import * as E from './mainNavElements'
import { useWindowSize } from 'helpers'
import { getCurrentResolutionBreakPoint } from 'utils/utilityFunctions'
import { useSelector, useDispatch } from 'react-redux'
import { fetchNextsteps } from 'actions/nextstepsActions'
import { httpVerbConstants } from 'constants/actionTypes'
import { NextStepValue } from 'utils/type'

const MenuExpander = (props) => {
  const [isActive, setIsActive] = useState(false)
  const decoratedOnClick = useAccordionToggle('0', () => {
    setIsActive(!isActive)
  })

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === 'NumpadEnter') {
      var selectedItem = document.activeElement
      if (selectedItem.id == 'mainnav.contactconsultant') {
        setIsActive(
          document.getElementById('mainnav.menuconsulantname') == null
        )
      }
      if (selectedItem.id == 'mainnav.mydetailsitem') {
        setIsActive(
          document.getElementById('mainnav.menudetailscustomername') == null
        )
      }
    }
  }

  return (
    <>
      <E.MenuAccordionTitleDiv onClick={decoratedOnClick} eventkey={'0'}>
        <E.MenuAccordionTitleSpan
          isActive={isActive}
          tabIndex={0}
          id={props.id}
          onKeyDown={handleKeyDown}
        >
          {props.title}
          <E.MenuAccordionArrow
            isActive={isActive}
            src="/images/nav-down-icon.png"
          />
        </E.MenuAccordionTitleSpan>
      </E.MenuAccordionTitleDiv>
      {isActive && (
        <E.MenuAccordionContainer>
          {props.contents
            .filter((item) => item.value || item.showWithNullValue)
            .map((item) => (
              <div className="mb-2">
                <p className="mb-0">{item.heading}</p>
                <strong id={item.id}>{item.value}</strong>
              </div>
            ))}
          {props.additionalComponent && (
            <E.MenuAccordionComponentDiv>
              {props.additionalComponent}
            </E.MenuAccordionComponentDiv>
          )}
        </E.MenuAccordionContainer>
      )}
    </>
  )
}

const MainNav = ({ applicationRoles, customer, consultant }) => {
  const { isConsultant } = applicationRoles
  const employer = customer.employer || {}
  const customerName = customer.firstName + ' ' + customer.lastName
  const consultantName = consultant.firstName + ' ' + consultant.lastName
  const estimatesLink = isConsultant ? '/consultant/estimates' : '/estimates'
  const settlementsLink = isConsultant
    ? '/consultant/settlements'
    : '/settlements'
  const userActivityLink = '/consultant/useractivity'
  const { ref, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false)

  let details = [
    {
      heading: 'Name',
      value: customerName,
      id: 'mainnav.menudetailscustomername',
    },
    {
      heading: 'Employer',
      value: employer.name,
      id: 'mainnav.menudetailsemployer',
    },
    {
      heading: 'Annual Salary',
      value: formatCurrency(employer.grossAnnualSalary),
      id: 'mainnav.menudetailssalary',
    },
    {
      heading: 'Pay cycle',
      value: employer.payFrequency,
      id: 'mainnav.menudetailspaycycle',
    },
  ]

  let contactConsultantDetails = [
    {
      heading: 'Consultant name',
      value: consultantName,
      id: 'mainnav.menuconsulantname',
    },
    {
      heading: 'Email',
      value: consultant.email,
      showWithNullValue: false,
      id: 'mainnav.menuconsulantemail',
    },
    {
      heading: 'Phone number',
      value: consultant.telephone,
      showWithNullValue: false,
      id: 'mainnav.menuconsulantphone',
    },
    {
      heading:
        consultant.firstName +
        ' is your personal consultant, dedicated to guiding you through this process. Want to loop someone into your decision? ' +
        consultant.firstName +
        ' can help bring them up to speed.',
      value: null,
      showWithNullValue: true,
      id: 'mainnav.menuconsulantblahblah',
    },
  ]

  let contactConsultantComponent = isConsultant ? (
    <PrimaryButton disabled>Request Call back</PrimaryButton>
  ) : (
    <ContactUsDialog
      className="text-brand font-weight-bold btn btn-primary text-white"
      theme="button"
      size="sm"
      extendInMob
      launchText="Request call back"
    />
  )

  const toggleMenuVisibility = () => {
    setIsComponentVisible(!isComponentVisible)
  }
  const resolutionBreakpoint = {
    nonDesktop: getCurrentResolutionBreakPoint(false),
  }
  const [width] = useWindowSize()
  let opportunity = useSelector((state) => state.opportunity)
  let currentFlowGraphState = 0
  if (opportunity.state && opportunity.state.toLowerCase() == 'won') {
    currentFlowGraphState = 5
  }

  let isProposalAccepted =
    applicationRoles &&
    applicationRoles.hasProposal &&
    applicationRoles.hasSubmittedEstimate &&
    applicationRoles.hasProposalAccepted &&
    opportunity &&
    opportunity.state &&
    opportunity.state !== 'Lost'

  const currentConsultantEmail = useSelector(
    (state) =>
      state.auth &&
      state.auth.idToken &&
      state.auth.idToken.claims &&
      state.auth.idToken.claims.email
  )
  const consultantCustomerViewValues =
    process.env.REACT_APP_CONSULTANTCUSTOMERVIEW.toLowerCase()

  const { opportunityId } = useSelector((state) => state.opportunity)
  const { accessToken } = useSelector((state) => state.auth)
  const dispatch = useDispatch()
  const [getNext, setGetNext] = useState(false)
  const headers = { accessToken: accessToken }
  const [showSkipToContent, setShowSkipToContent] = useState(false)

  useEffect(() => {
    if (opportunityId && isProposalAccepted) {
      dispatch(
        fetchNextsteps(opportunityId, headers, httpVerbConstants.HTTP_GET)
      )
    }
  }, [getNext])

  useEffect(() => {
    const listener = (event) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        var myquoteMenuItem = document.getElementById('mainnav.myquoteitem')
        setIsComponentVisible(myquoteMenuItem == null)
      }
    }
    var menudivEl = document.getElementById('mainnav.menutitlediv')
    if (menudivEl) menudivEl.addEventListener('keydown', listener)
  }, [])

  useEffect(() => {
    const listener = (event) => {
      if (event.code === 'Escape') setIsComponentVisible(false)
      if (
        event.code === 'Tab' &&
        window.location.href.toLowerCase().indexOf('home') != -1
      ) {
        setShowSkipToContent(true)
      }
    }
    document.addEventListener('keydown', listener)
  }, [])

  if (opportunityId && !getNext && currentFlowGraphState == 0) {
    setGetNext(true)
  }

  let currentStep
  useSelector((state) => (currentStep = state.nextSteps.step))
  let settlement
  useSelector((state) => (settlement = state.opportunity.settlement))
  if (currentFlowGraphState != 5) {
    switch (currentStep) {
      case NextStepValue.ATO: {
        currentFlowGraphState = 2
        break
      }
      case NextStepValue.DEFERRED:
      case NextStepValue.INSURANCE: {
        currentFlowGraphState = 3
        break
      }
      case NextStepValue.NONE: {
        if (applicationRoles.hasProposalAccepted)
          if (
            settlement &&
            settlement.recipients &&
            settlement.recipients.filter((r) => !r.hasSigned) == 0
          )
            currentFlowGraphState = 4
          else currentFlowGraphState = 3

        break
      }
      default: {
        currentFlowGraphState = 0
      }
    }
    if (
      applicationRoles &&
      !applicationRoles.hasProposalAccepted &&
      applicationRoles.hasProposal
    ) {
      currentFlowGraphState = 2
    }
    if (currentFlowGraphState == 0) {
      currentFlowGraphState = 1
    }
  }

  let settlementMenuItemVisible = useSelector(
    (state) =>
      state.opportunity.settlement &&
      state.opportunity.settlement.agreementId &&
      applicationRoles.hasProposalAccepted &&
      currentStep == NextStepValue.NONE
    ) //Either doing settlement or has completed it

  //If currently on settlments page, don't show the menu item for it.
  var url = window.location.href.toLowerCase()
  if (url.indexOf('settlements') !== -1) {
    settlementMenuItemVisible = false
  }

  return (
    <>
      {applicationRoles && applicationRoles.hasProposalAccepted && (
        <div id="notification" className="text-center">
          You have accepted a proposal. Contact your consultant if you have any
          more questions
        </div>
      )}
      <E.MainNavContainer>
        <E.MainLogoContainer>
          <span>
            Visited link, Oly DEP site logo button-Pressing this button
            takes you to the home page of Oly DEP site
          </span>
          <Link
            to={isConsultant ? '/consultant/home' : '/home'}
            tabIndex={
              window.location.href.toLowerCase().indexOf('home') != -1 ? -1 : 0 //if on home page home link not keyboard focusable. Initial focus to Skip to main content button
            }
          >
            <Icon iconType="OlyLogo" size={3} />            
          </Link>
        </E.MainLogoContainer>
        {width >= resolutionBreakpoint.nonDesktop && (
          <>
            {window.location.href.toLowerCase().indexOf('home') != -1 &&
              showSkipToContent && (
                <PrimaryButton
                  onClick={() => document.getElementById('maincontent').focus()}
                  invertColor
                >
                  Skip to main content
                </PrimaryButton>
              )}
            <E.MainFlowBlock>
              <FlowGraph
                brand="oly"
                activeTill={currentFlowGraphState + 1}
                items={[
                  'Get Quote',
                  'Finance Approval',
                  'Order Car',
                  'Paperwork',
                  'Drive Time',
                ]}
              ></FlowGraph>
            </E.MainFlowBlock>
          </>
        )}
        {(isConsultant || isLoggedIn()) && (
          <div>
            <E.MenuTitleDiv isShowing={isComponentVisible} ref={ref}>
              <div
                style={{
                  float: 'right',
                  display: 'flex',
                  alignItems: 'right',
                  cursor: 'pointer',
                  width: '120px',
                  position: 'absolute',
                  top:'30px',
                  right:'0px'
                }}
                id="mainnav.menutitlediv"
                tabIndex={0}
                onClick={toggleMenuVisibility}
              >
                <E.MenuWordDiv>
                  {!isComponentVisible ? 'Menu' : 'Close'}
                </E.MenuWordDiv>

                <E.MenuHamburgerImg
                  src={
                    !isComponentVisible
                      ? '/images/hamburger.svg'
                      : '/images/hamburger-close.svg'
                  }
                />
              </div>
              {isComponentVisible && (
                <E.MenuContainingDiv>
                  <E.MenuIconDiv>                   
                    <Icon iconType="OlyLogo" size={1.5} />
                  </E.MenuIconDiv>
                  <div className="dropdown-divider mb-3" />
                  <E.MenuLinkTitle to={estimatesLink} id="mainnav.myquoteitem">
                    My quote
                  </E.MenuLinkTitle>
                  <div className="dropdown-divider mb-3" />
                  <MenuExpander
                    title="My details"
                    contents={details}
                    id="mainnav.mydetailsitem"
                  />
                  <div className="dropdown-divider mb-3" />
                  {settlementMenuItemVisible && (
                    <div>
                      <E.MenuLinkTitle
                        to={settlementsLink}
                        id="mainnav.settlements"
                      >
                        Paperwork
                      </E.MenuLinkTitle>
                      <div className="dropdown-divider mb-3" />
                    </div>
                  )}
                  <MenuExpander
                    title="Contact consultant"
                    contents={contactConsultantDetails}
                    additionalComponent={contactConsultantComponent}
                    id="mainnav.contactconsultant"
                  />
                  <div className="dropdown-divider mb-3" />
                  <div className="ml-3 mb-3">
                    {isConsultant ? (
                      <ConsultantLogoutButton />
                    ) : (
                      <LogoutButton />
                    )}
                  </div>
                </E.MenuContainingDiv>
              )}
            </E.MenuTitleDiv>
          </div>
        )}
      </E.MainNavContainer>
      {width < resolutionBreakpoint.nonDesktop && (
        <E.MainFlowBlockMobile>
          <FlowGraph
            brand="oly"
            activeTill={currentFlowGraphState + 1}
            items={[
              'Get Quote',
              'Finance Approval',
              'Order Car',
              'Paperwork',
              'Drive Time',
            ]}
          />
        </E.MainFlowBlockMobile>
      )}
    </>
  )
}
export default MainNav
